import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Compass, Crown, Play, TrendingUp, Globe, Star } from "lucide-react";
import api from "@/lib/api";
import { SongCard, SectionHeader } from "@/components/SongCard";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://images.pexels.com/photos/18570541/pexels-photo-18570541.jpeg";

function Row({ title, subtitle, songs = [] }) {
  if (!songs.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-16">
      <SectionHeader title={title} subtitle={subtitle} action={<Link to="/songs" className="text-sm text-neon-cyan hover:underline whitespace-nowrap">View all</Link>} />
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        {songs.map((s, i) => <div key={s.song_id} className="w-40 sm:w-48 flex-shrink-0"><SongCard song={s} index={i} /></div>)}
      </div>
    </section>
  );
}

export default function Home() {
  const [feed, setFeed] = useState(null);
  const [singers, setSingers] = useState([]);

  useEffect(() => {
    api.get("/songs/home").then(({ data }) => setFeed(data)).catch(() => {});
    api.get("/community/singers").then(({ data }) => setSingers(data.items)).catch(() => {});
  }, []);

  return (
    <div className="pb-10">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07050A]/60 via-[#07050A]/70 to-[#07050A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-5">
              <Globe className="w-4 h-4" /> A global stage for every voice
            </span>
            <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              SINGVI<br /><span className="text-gradient">KARAOKE</span>
            </h1>
            <p className="font-head text-2xl sm:text-3xl font-bold mt-4">Sing. Record. Share.</p>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 max-w-lg">Your voice. Your song. Your stage. Sing licensed karaoke tracks, shape your vocals with pro tools, and share with the world.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/record"><Button data-testid="hero-start-singing" className="rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold px-8 py-6 text-base"><Mic className="w-5 h-5 mr-2" /> Start Singing</Button></Link>
              <Link to="/songs"><Button variant="outline" data-testid="hero-explore" className="rounded-full glass border-white/20 hover:bg-white/10 font-bold px-8 py-6 text-base"><Compass className="w-5 h-5 mr-2" /> Explore Songs</Button></Link>
            </div>
            <div className="flex gap-6 mt-10 text-sm">
              <Link to="/register" className="text-white/80 hover:text-neon-cyan transition-colors font-semibold">Join Free →</Link>
              <Link to="/premium" className="text-white/80 hover:text-neon-cyan transition-colors font-semibold flex items-center gap-1"><Crown className="w-4 h-4" /> Go Premium →</Link>
            </div>
          </div>
        </div>
      </section>

      {feed && (
        <>
          <Row title="Trending Songs" subtitle="What the world is singing right now" songs={feed.trending} />
          <Row title="New Releases" subtitle="Freshly added karaoke" songs={feed.new_releases} />

          {/* Popular singers */}
          {singers.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-16">
              <SectionHeader title="Popular Singers" subtitle="Rising voices in the community" action={<Link to="/community" className="text-sm text-neon-cyan hover:underline">Community</Link>} />
              <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
                {singers.map((s) => (
                  <Link key={s.username} to={`/@${s.username}`} className="w-32 flex-shrink-0 text-center group">
                    <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-2 border-white/10 group-hover:border-neon-pink transition-colors bg-neon-purple/20 flex items-center justify-center">
                      {s.avatar_url ? <img src={s.avatar_url.startsWith("http") ? s.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${s.avatar_url}`} className="w-full h-full object-cover" alt="" /> : <span className="text-2xl font-head font-bold">{(s.display_name || s.username)[0].toUpperCase()}</span>}
                    </div>
                    <p className="text-sm font-semibold mt-2 truncate">@{s.username}</p>
                    <p className="text-xs text-muted-foreground">{s.followers} followers</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <Row title="Punjabi Hits" songs={feed.punjabi} />
          <Row title="Bollywood" songs={feed.bollywood} />
          <Row title="K-Pop" songs={feed.kpop} />
          <Row title="Latin" songs={feed.latin} />
          <Row title="Rap & Hip-Hop" songs={feed.rap} />
          <Row title="English Hits" songs={feed.english} />
        </>
      )}

      {/* Premium CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
        <div className="glass rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-neon-pink/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-neon-cyan/20 rounded-full blur-3xl" />
          <div className="relative">
            <Crown className="w-10 h-10 text-neon-pink mx-auto mb-4" />
            <h2 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Take Your Voice Further</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Unlimited recordings, the full professional mixer, advanced effects and eligible downloads.</p>
            <Link to="/premium"><Button className="rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold px-8 py-6 mt-6" data-testid="home-go-premium">Go Premium — from CA$46.99/yr</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
